import moment from 'moment';
import { IUserSettings } from '@api/User.api';
import getCharge from '@utils/getCharge';

export interface IRawTelemetry {
    ts_s: number;
    ts_d?: number;
    lat: number | null;
    lon: number | null;
    lat_lbs: number | null;
    lon_lbs: number | null;
    address: string;
    speed: number;
    dir: number;
    alt: number;
    sats: number;
    hdop: number;
    bat_v: number;
    event: string;
    cur_sim: number;
    sim1pr: number;
    sim2pr: number;
    pm: number;
    // alarm1: number;
    // alarm2: number;
    // alarm3: number;
    // alarm4: number;
    // alarm5: number;
    // alarm6: number;
    signal: number;
    temp: number;
    fv: number;
}

export interface ICoords {
    lat: number | null;
    lon: number | null;
}

export interface ITelemetry {
    date: string | null;
    coords: string | null;
    coordType: string | null;
    speed: number;
    dir: number;
    altitude: number;
    address: string
    satellitesNumber: number;
    hdop: number;
    batteryVoltage: string | null;
    event: string;
    cur_sim: string;
    sim1pr: string;
    sim2pr: string;
    pm: string;
    alarm1: string;
    alarm2: string;
    alarm3: string;
    alarm4: string;
    alarm5: string;
    alarm6: string;
    signal: number;
    temperature: number;
}

export interface ITelemetry2 extends ITelemetry{
    number?: number | null;
}

type telemetryArray = [
    string | null,
    string | null,
    string | null,
    number,
    number,
    number,
    number,
    number,
    string | null,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    number,
    number,
];

export default class Telemetry {
    public static headers = [
        'number',
        'date',
        'coords',
        'address',
        'coordType',
        'speed',
        'dir',
        'altitude',
        'satellitesNumber',
        'hdop',
        'batteryVoltage',
        'event',
        'cur_sim',
        'sim1pr',
        'sim2pr',
        'pm',
        // 'alarm1',
        // 'alarm2',
        // 'alarm3',
        // 'alarm4',
        // 'alarm5',
        // 'alarm6',
        
        'signal',
        'temperature',
        // 'trailerVin',
        // 'contractNumber',
        // 'company'
    ];
    public static units = {
        speed: 'km/h',
        dir: '°',
        altitude: 'm',
        batteryVoltage: 'V',
        temperature: '°C',
        batteryCharge: '%',
    };
    public number: number;
    public speed: number;
    public dir: number;
    public altitude: number;
    public address: string;
    public satellitesNumber: number;
    public hdop: number;
    public batteryVoltage: string | null;
    public event: string;
    public cur_sim: string;
    public sim1pr: string;
    public sim2pr: string;
    public pm: string;
    public alarm1: string;
    public alarm2: string;
    public alarm3: string;
    public alarm4: string;
    public alarm5: string;
    public alarm6: string;
    public signal: number;
    public temperature: number;
    public date: string | null;
    public coords: string | null;
    public coordType: string | null;
    public trailerVin: string;
    public contractNumber: string;
    public company: string;

    constructor(data: IRawTelemetry, options: Pick<IUserSettings, 'timezone'>) {
        const {
            ts_s,
            lat,
            lon,
            lat_lbs,
            lon_lbs,
            speed,
            dir,
            alt,
            sats,
            hdop,
            bat_v,
            event,
            cur_sim,
            sim1pr,
            sim2pr,
            pm,
            // alarm1,
            // alarm2,
            // alarm3,
            // alarm4,
            // alarm5,
            // alarm6,
            signal,
            temp,
            fv,
            address,

        } = data;
        const { timezone } = options;
        this.speed = speed;
        this.dir = dir;
        this.altitude = alt;
        this.satellitesNumber = sats;
        this.hdop = hdop;
        this.address = address;
        //this.batteryVoltage = bat_v ? bat_v.toFixed(2) : null;
        this.batteryVoltage = 
            Number.isInteger(bat_v) 
                ? `${bat_v} %` 
                : bat_v
                    ? bat_v.toFixed(2)
                    : null;
        this.event = typeof event === 'string' ? event.toLowerCase() : '';
        this.cur_sim = this.getCurSim(cur_sim);
        this.sim1pr = this.getSimPr(sim1pr);
        this.sim2pr = this.getSimPr(sim2pr);
        this.pm = this.getOnOff(pm);
        // this.alarm1 = this.getOnOff(alarm1);
        // this.alarm2 = this.getOnOff(alarm2);
        // this.alarm3 = this.getOnOff(alarm3);
        // this.alarm4 = this.getOnOff(alarm4);
        // this.alarm5 = this.getOnOff(alarm5);
        // this.alarm6 = this.getOnOff(alarm6);    
        if (signal === 4) {
            this.signal = 31;
        } else if (signal === 3) {
            this.signal = 23;
        } else if (signal === 2) {
            this.signal = 15;
        } else if (signal === 1) {
            this.signal = 10;
        } else if (signal === 0) {
            this.signal = 0;
        } else {
            this.signal = signal;
        }

        this.temperature = temp;
        this.date = ts_s
            ? moment(ts_s).utcOffset(timezone).format('HH:mm DD.MM.YYYY')
            : null;
        const gpsCoord: ICoords = { lat, lon };
        const lbsCoord: ICoords = { lat: lat_lbs, lon: lon_lbs };
        this.coords = this.getCoord(gpsCoord, lbsCoord);
        this.coordType = this.getCoordType(gpsCoord, lbsCoord);
    }

    public getObject(): ITelemetry {
        return {
            date: this.date,
            coords: this.coords,
            coordType: this.coordType,
            speed: this.speed,
            dir: this.dir,
            altitude: this.altitude,
            satellitesNumber: this.satellitesNumber,
            hdop: this.hdop,
            batteryVoltage: this.batteryVoltage,
            event: this.event,
            cur_sim: this.cur_sim,
            sim1pr: this.sim1pr,
            sim2pr: this.sim2pr,
            pm: this.pm,
            alarm1: this.alarm1,
            alarm2: this.alarm2,
            alarm3: this.alarm3,
            alarm4: this.alarm4,
            alarm5: this.alarm5,
            alarm6: this.alarm6,
            signal: this.signal,
            temperature: this.temperature,
            address: this.address,
        };
    }

    public getArray(): telemetryArray {
        return [
            this.date,
            this.coords,
            this.coordType,
            this.speed,
            this.dir,
            this.altitude,
            this.satellitesNumber,
            this.hdop,
            this.batteryVoltage,
            this.event,
            this.cur_sim,
            this.sim1pr,
            this.sim2pr,
            this.pm,
            this.alarm1,
            this.alarm2,
            this.alarm3,
            this.alarm4,
            this.alarm5,
            this.alarm6,
            this.signal,
            this.temperature,
        ];
    }

    public getEvent(code: number) {
        switch (code) {
            case 0:
                return 'FIRST_START';
            case 1:
                return 'PERIODIC';
            case 2:
                return 'ALARM_1';
            case 3:
                return 'ALARM_2';
            case 4:
                return 'ALARM_3';
            case 5:
                return 'ALARM_4';
            case 6:
                return 'ALARM_5';
            case 7:
                return 'ALARM_6';
            case 8:
                return 'MOTION_DETECT';
            case 9:
                return 'STOP_DETECT';
            case 10:
                return 'LIGTH_APPEARANCE';
            case 11:
                return 'LIGHT_DISAPPEARANCE';
            default:
                return '';
        }
    }

    public getCurSim(code: number) {
        switch (code) {
            case 1:
                return 'SIM 1';
            case 2:
                return 'SIM 2';
            default:
                return '';
        }
    }

    public getSimPr(code: number) {
        switch (code) {
            case 0:
                return 'missing';
            case 1:
                return 'present';
            default:
                return '';
        }
    }

    public getOnOff(code: number) {
        switch (code) {
            case 0:
                return 'notActive';
            case 1:
                return 'active';
            default:
                return '';
        }
    }

    public getCoord(gpsCoord: ICoords, lbsCoord: ICoords) {
        let _lat: number, _lon: number;
        if (gpsCoord && gpsCoord.lat && gpsCoord.lon) {
            _lat = gpsCoord.lat;
            _lon = gpsCoord.lon;
        } else if (lbsCoord && lbsCoord.lat && lbsCoord.lon) {
            _lat = lbsCoord.lat;
            _lon = lbsCoord.lon;
        } else return null;
        const lat = _lat.toFixed(6);
        const lon = _lon.toFixed(6);
        return `${lat}, ${lon}`;
    }

    public getCoordType(gpsCoord: ICoords, lbsCoord: ICoords) {
        if (gpsCoord && gpsCoord.lat && gpsCoord.lon) {
            return 'gps';
        } else if (lbsCoord && lbsCoord.lat && lbsCoord.lon) {
            return 'lbs';
        } else return null;
    }
}
